import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import CatalogCsvTab from "../components/CatalogCsvTab";

const AdminPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', category: 'Metal Polish',
    vendor: 'Cut Above Polishing and Detailing', vendor_sku: '',
    vendor_cost: 0, online_price: 0, msrp: '', inventory_count: 0,
    image_url: '', images: [], is_active: true, fulfillment: 'Stock',
    has_cut_above_logo: false, weight_lbs: 1,
  });
  const [creatingProduct, setCreatingProduct] = useState(false);

  // Videos state
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: '', category: 'polishing101', video_url: '', thumbnail_url: '', duration_minutes: 10, is_vip_only: false, related_product_ids: [] });
  const [creatingVideo, setCreatingVideo] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [savingVideo, setSavingVideo] = useState(false);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [togglingVip, setTogglingVip] = useState(null);

  // VIP Members state
  const [vipMembers, setVipMembers] = useState([]);
  const [loadingVip, setLoadingVip] = useState(false);
  const [extendingVip, setExtendingVip] = useState(null); // user_id being extended
  const [extendMonths, setExtendMonths] = useState({});   // { user_id: months }

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [updatingOrder, setUpdatingOrder] = useState(null);

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({ author: '', location: '', content: '', rating: 5 });
  const [creatingTestimonial, setCreatingTestimonial] = useState(false);

  // Affiliates state
  const [affiliates, setAffiliates] = useState([]);
  const [loadingAffiliates, setLoadingAffiliates] = useState(false);
  const [newAffiliate, setNewAffiliate] = useState({ name: '', location: '', description: '', links: { website: '', maps: '', facebook: '', twitter: '', tiktok: '', youtube: '' } });
  const [creatingAffiliate, setCreatingAffiliate] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [savingAffiliate, setSavingAffiliate] = useState(false);

  // Promo state
  const [promo, setPromo] = useState(null);
  const [loadingPromo, setLoadingPromo] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);

  // Referrals state
  const [referralData, setReferralData] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  // Wholesale state
  const [wholesaleApps, setWholesaleApps] = useState([]);
  const [wholesaleTiers, setWholesaleTiers] = useState([]);
  const [loadingWholesale, setLoadingWholesale] = useState(false);
  const [reviewingApp, setReviewingApp] = useState(null);
  const [reviewTier, setReviewTier] = useState(1);
  const [reviewNotes, setReviewNotes] = useState('');

  // Reviews moderation state
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  useEffect(() => {
    if (!token || !user?.is_admin) return;
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } });
        setProducts(response.data);
      } catch (error) {
        toast.error('Failed to load products');
      }
      setLoadingProducts(false);
    };
    fetchProducts();
  }, [token, user]);

  useEffect(() => {
    if (activeTab === 'videos' && token && user?.is_admin) {
      setLoadingVideos(true);
      axios.get(`${API}/videos`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setVideos(r.data))
        .catch(() => toast.error('Failed to load videos'))
        .finally(() => setLoadingVideos(false));
    }
    if (activeTab === 'affiliates' && token && user?.is_admin) {
      setLoadingAffiliates(true);
      axios.get(`${API}/affiliates`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setAffiliates(r.data))
        .catch(() => toast.error('Failed to load affiliates'))
        .finally(() => setLoadingAffiliates(false));
    }
    if (activeTab === 'customers' && token && user?.is_admin) {
      setLoadingCustomers(true);
      axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCustomers(r.data))
        .catch(() => toast.error('Failed to load customers'))
        .finally(() => setLoadingCustomers(false));
    }
    if (activeTab === 'orders' && token && user?.is_admin) {
      setLoadingOrders(true);
      axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setOrders(r.data))
        .catch(() => toast.error('Failed to load orders'))
        .finally(() => setLoadingOrders(false));
    }
    if (activeTab === 'vip' && token && user?.is_admin) {
      setLoadingVip(true);
      axios.get(`${API}/admin/vip-members`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setVipMembers(r.data))
        .catch(() => toast.error('Failed to load VIP members'))
        .finally(() => setLoadingVip(false));
    }
    if (activeTab === 'testimonials' && token && user?.is_admin) {
      setLoadingTestimonials(true);
      axios.get(`${API}/testimonials`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setTestimonials(r.data))
        .catch(() => toast.error('Failed to load testimonials'))
        .finally(() => setLoadingTestimonials(false));
    }
    if (activeTab === 'promo' && token && user?.is_admin) {
      setLoadingPromo(true);
      axios.get(`${API}/admin/promo`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setPromo(r.data))
        .catch(() => toast.error('Failed to load promo settings'))
        .finally(() => setLoadingPromo(false));
    }
    if (activeTab === 'referrals' && token && user?.is_admin) {
      setLoadingReferrals(true);
      axios.get(`${API}/admin/referrals`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setReferralData(r.data))
        .catch(() => toast.error('Failed to load referrals'))
        .finally(() => setLoadingReferrals(false));
    }
    if (activeTab === 'wholesale' && token && user?.is_admin) {
      setLoadingWholesale(true);
      Promise.all([
        axios.get(`${API}/admin/wholesale/applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/wholesale/tiers`, { headers: { Authorization: `Bearer ${token}` } }),
      ]).then(([a, t]) => {
        setWholesaleApps(a.data.applications || []);
        setWholesaleTiers(t.data.tiers || []);
      }).catch(() => toast.error('Failed to load wholesale')).finally(() => setLoadingWholesale(false));
    }
    if (activeTab === 'reviews' && token && user?.is_admin) {
      setLoadingReviews(true);
      axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setReviews(r.data || []))
        .catch(() => toast.error('Failed to load reviews'))
        .finally(() => setLoadingReviews(false));
    }
  }, [activeTab, token, user]);

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-heading text-3xl mb-4">Admin Access Required</h1>
          <p className="text-gray-400 mb-6">You must be logged in as an admin to view this page.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider">Login as Admin</Link>
        </div>
      </div>
    );
  }

  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    setSavingProduct(true);
    try {
      const payload = {
        name: editingProduct.name, description: editingProduct.description,
        category: editingProduct.category, vendor: editingProduct.vendor,
        vendor_sku: editingProduct.vendor_sku,
        vendor_cost: Number(editingProduct.vendor_cost) || 0,
        online_price: Number(editingProduct.online_price) || 0,
        msrp: editingProduct.msrp === '' || editingProduct.msrp === null || editingProduct.msrp === undefined ? null : Number(editingProduct.msrp),
        inventory_count: Number(editingProduct.inventory_count) || 0,
        image_url: editingProduct.image_url, is_active: editingProduct.is_active,
        images: editingProduct.images || [],
        fulfillment: editingProduct.fulfillment || 'Stock',
        has_cut_above_logo: !!editingProduct.has_cut_above_logo,
        weight_lbs: Number(editingProduct.weight_lbs) || 1,
      };
      const response = await axios.put(`${API}/admin/products/${editingProduct.product_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(prev => prev.map(p => p.product_id === response.data.product_id ? response.data : p));
      setEditingProduct(null);
      toast.success('Product updated');
    } catch (error) {
      toast.error('Failed to save product');
    }
    setSavingProduct(false);
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setCreatingProduct(true);
    try {
      const payload = { ...newProduct, vendor_cost: Number(newProduct.vendor_cost) || 0, online_price: Number(newProduct.online_price) || 0, msrp: newProduct.msrp === '' || newProduct.msrp === null ? null : Number(newProduct.msrp), inventory_count: Number(newProduct.inventory_count) || 0, weight_lbs: Number(newProduct.weight_lbs) || 1 };
      const response = await axios.post(`${API}/products`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(prev => [response.data, ...prev]);
      setNewProduct({ name: '', description: '', category: 'Metal Polish', vendor: 'Cut Above Polishing and Detailing', vendor_sku: '', vendor_cost: 0, online_price: 0, msrp: '', inventory_count: 0, image_url: '', images: [], is_active: true, fulfillment: 'Stock', has_cut_above_logo: false, weight_lbs: 1 });
      toast.success('Product created');
    } catch (error) {
      toast.error('Failed to create product');
    }
    setCreatingProduct(false);
  };

  const handleCreateVideo = async (e) => {
    e.preventDefault();
    setCreatingVideo(true);
    try {
      const response = await axios.post(`${API}/videos`, newVideo, { headers: { Authorization: `Bearer ${token}` } });
      setVideos(prev => [response.data, ...prev]);
      setNewVideo({ title: '', category: 'polishing101', video_url: '', thumbnail_url: '', duration_minutes: 10, is_vip_only: false, related_product_ids: [] });
      toast.success('Video added');
    } catch (error) {
      toast.error('Failed to add video');
    }
    setCreatingVideo(false);
  };

  const handleSaveVideo = async () => {
    if (!editingVideo) return;
    setSavingVideo(true);
    try {
      const payload = {
        title: editingVideo.title,
        category: editingVideo.category,
        video_url: editingVideo.video_url,
        thumbnail_url: editingVideo.thumbnail_url || '',
        duration_minutes: Number(editingVideo.duration_minutes) || 0,
        is_vip_only: !!editingVideo.is_vip_only,
        related_product_ids: editingVideo.related_product_ids || [],
      };
      const res = await axios.put(`${API}/videos/${editingVideo.video_id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setVideos(prev => prev.map(v => v.video_id === editingVideo.video_id ? res.data : v));
      setEditingVideo(null);
      toast.success('Video updated');
    } catch { toast.error('Failed to update video'); }
    setSavingVideo(false);
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      await axios.delete(`${API}/videos/${videoId}`, { headers: { Authorization: `Bearer ${token}` } });
      setVideos(prev => prev.filter(v => v.video_id !== videoId));
      toast.success('Video deleted');
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    setCreatingAffiliate(true);
    try {
      const response = await axios.post(`${API}/admin/affiliates`, newAffiliate, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(prev => [response.data, ...prev]);
      setNewAffiliate({ name: '', location: '', description: '', links: { website: '', maps: '', facebook: '', twitter: '', tiktok: '', youtube: '' } });
      toast.success('Affiliate added');
    } catch (error) {
      toast.error('Failed to add affiliate');
    }
    setCreatingAffiliate(false);
  };

  const handleSaveAffiliate = async () => {
    if (!editingAffiliate) return;
    setSavingAffiliate(true);
    try {
      const res = await axios.put(`${API}/admin/affiliates/${editingAffiliate.affiliate_id}`, editingAffiliate, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(prev => prev.map(a => a.affiliate_id === editingAffiliate.affiliate_id ? res.data : a));
      setEditingAffiliate(null);
      toast.success('Affiliate updated');
    } catch { toast.error('Failed to update affiliate'); }
    setSavingAffiliate(false);
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    try {
      await axios.delete(`${API}/admin/affiliates/${affiliateId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(prev => prev.filter(a => a.affiliate_id !== affiliateId));
      toast.success('Affiliate deleted');
    } catch (error) {
      toast.error('Failed to delete affiliate');
    }
  };

  const handleCreateTestimonial = async (e) => {
    e.preventDefault();
    setCreatingTestimonial(true);
    try {
      const response = await axios.post(`${API}/admin/testimonials`, { ...newTestimonial, rating: Number(newTestimonial.rating) }, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(prev => [response.data, ...prev]);
      setNewTestimonial({ author: '', location: '', content: '', rating: 5 });
      toast.success('Testimonial added');
    } catch { toast.error('Failed to add testimonial'); }
    setCreatingTestimonial(false);
  };

  const handleDeleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(prev => prev.filter(t => t.testimonial_id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSeedTestimonials = async () => {
    try {
      const res = await axios.post(`${API}/admin/seed-testimonials`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      const r = await axios.get(`${API}/testimonials`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(r.data);
    } catch { toast.error('Failed to seed'); }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    setUpdatingOrder(orderId);
    try {
      // If shipping, prompt for tracking number
      const payload = { status };
      if (status === 'shipped') {
        const tn = window.prompt('Enter FedEx tracking number (optional — press OK to skip):', '');
        if (tn && tn.trim()) {
          payload.tracking_number = tn.trim();
          payload.tracking_carrier = 'FedEx';
        }
      }
      await axios.put(`${API}/admin/orders/${orderId}/status`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: status, ...(payload.tracking_number ? { tracking_number: payload.tracking_number, tracking_carrier: 'FedEx' } : {}) } : o));
      toast.success('Order status updated');
    } catch { toast.error('Failed to update order'); }
    setUpdatingOrder(null);
  };

  const handleToggleVip = async (userId, currentVipStatus, customerName) => {
    setTogglingVip(userId);
    try {
      const newStatus = !currentVipStatus;
      const res = await axios.put(
        `${API}/admin/users/${userId}/vip`,
        { is_vip: newStatus, months: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update in customers list
      setCustomers(prev => prev.map(c => c.user_id === userId ? { ...c, is_vip: newStatus, vip_expires_at: res.data.vip_expires_at } : c));
      // Update in vip members list
      if (newStatus) {
        setVipMembers(prev => [...prev.filter(v => v.user_id !== userId), res.data]);
      } else {
        setVipMembers(prev => prev.filter(v => v.user_id !== userId));
      }
      toast.success(`${customerName} ${newStatus ? 'granted' : 'revoked'} VIP access`);
    } catch { toast.error('Failed to update VIP status'); }
    setTogglingVip(null);
  };

  const handleExtendVip = async (userId, memberName) => {
    const months = parseInt(extendMonths[userId] || 1);
    setExtendingVip(userId);
    try {
      const res = await axios.put(
        `${API}/admin/users/${userId}/vip`,
        { is_vip: true, months },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVipMembers(prev => prev.map(m => m.user_id === userId ? { ...m, ...res.data } : m));
      setCustomers(prev => prev.map(c => c.user_id === userId ? { ...c, ...res.data } : c));
      setExtendMonths(prev => ({ ...prev, [userId]: 1 }));
      toast.success(`${memberName}'s VIP extended by ${months} month${months > 1 ? 's' : ''}`);
    } catch { toast.error('Failed to extend VIP'); }
    setExtendingVip(null);
  };

  const inputCls = "w-full bg-[#0F0F0F] border border-white/10 rounded-sm px-3 py-2 text-white text-sm";
  const tabBtnCls = (t) => `px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wide border ${activeTab === t ? 'bg-[#D62828] border-[#D62828] text-white' : 'bg-[#121212] border-white/10 text-gray-300'}`;

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl tracking-wide mb-6">ADMIN DASHBOARD</h1>

        <div className="flex flex-wrap gap-3 mb-8">
          {['products', 'catalog', 'videos', 'affiliates', 'testimonials', 'reviews', 'customers', 'vip', 'orders', 'promo', 'referrals', 'wholesale'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={tabBtnCls(t)} data-testid={`admin-tab-${t}`}>{t === 'vip' ? 'VIP Members' : t === 'promo' ? 'Promo' : t === 'referrals' ? 'Referrals' : t === 'wholesale' ? 'Wholesale' : t === 'reviews' ? 'Reviews' : t === 'catalog' ? 'Bulk CSV' : t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <div data-testid="admin-products">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-8">
              <h2 className="font-heading text-2xl mb-4">Create New Product</h2>
              <form onSubmit={handleCreateProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {[['Name', 'name', 'text'], ['Category', 'category', 'text'], ['Vendor', 'vendor', 'text'], ['Vendor SKU', 'vendor_sku', 'text'], ['Vendor Cost', 'vendor_cost', 'number'], ['Online Price', 'online_price', 'number'], ['MSRP / Listed Price (optional)', 'msrp', 'number'], ['Inventory Count', 'inventory_count', 'number'], ['Image URL', 'image_url', 'text'], ['Weight (lbs)', 'weight_lbs', 'number']].map(([label, field, type]) => (
                  <div key={field}>
                    <label className="block text-gray-400 mb-1">{label}</label>
                    <input type={type} step={type === 'number' ? '0.01' : undefined} value={newProduct[field]} onChange={e => setNewProduct(prev => ({ ...prev, [field]: e.target.value }))} className={inputCls} required={['name', 'category', 'vendor', 'vendor_sku'].includes(field)} />
                  </div>
                ))}
                <div>
                  <label className="block text-gray-400 mb-1">Description</label>
                  <textarea value={newProduct.description} onChange={e => setNewProduct(prev => ({ ...prev, description: e.target.value }))} className={`${inputCls} min-h-[80px]`} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 mb-1">Additional Image URLs <span className="text-gray-500 text-xs">(comma-separated — enables carousel)</span></label>
                  <input
                    type="text"
                    placeholder="https://...img1.jpg, https://...img2.jpg"
                    value={(newProduct.images || []).join(', ')}
                    onChange={e => setNewProduct(prev => ({ ...prev, images: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                    className={inputCls}
                    data-testid="new-product-images"
                  />
                </div>
                <div className="flex items-center mt-6">
                  <button type="submit" disabled={creatingProduct} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm font-semibold uppercase tracking-wide text-xs disabled:opacity-50">
                    {creatingProduct ? 'Saving...' : 'Save Product'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">Existing Products</h2>
              {loadingProducts ? (
                <div className="text-center py-8 text-gray-400">Loading products...</div>
              ) : (
                <div className="overflow-x-auto text-sm">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-400">
                        {['Name', 'SKU', 'Category', 'Vendor', 'Price', 'Stock', 'Active', ''].map(h => <th key={h} className="text-left py-2 pr-4">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => (
                        <tr key={p.product_id} className="border-b border-white/5 last:border-0">
                          <td className="py-2 pr-4 text-white">{p.name}</td>
                          <td className="py-2 pr-4 text-gray-300">{p.vendor_sku}</td>
                          <td className="py-2 pr-4 text-gray-300">{p.category}</td>
                          <td className="py-2 pr-4 text-gray-300">{p.vendor}</td>
                          <td className="py-2 pr-4 text-[#D62828] font-semibold">${p.online_price.toFixed(2)}</td>
                          <td className="py-2 pr-4 text-gray-300">{p.inventory_count}</td>
                          <td className="py-2 pr-4 text-gray-300">{p.is_active ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-4">
                            <button onClick={() => setEditingProduct(p)} className="text-xs px-3 py-1 rounded-sm border border-white/20 text-gray-200 hover:border-[#D62828]">Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {editingProduct && (
                <div className="mt-6 border-t border-white/10 pt-4">
                  <h3 className="font-heading text-xl mb-3">Edit Product</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {[['Name', 'name', 'text'], ['Category', 'category', 'text'], ['Vendor', 'vendor', 'text'], ['Vendor SKU', 'vendor_sku', 'text'], ['Vendor Cost', 'vendor_cost', 'number'], ['Online Price', 'online_price', 'number'], ['MSRP / Listed Price (optional)', 'msrp', 'number'], ['Inventory Count', 'inventory_count', 'number'], ['Image URL', 'image_url', 'text'], ['Weight (lbs)', 'weight_lbs', 'number']].map(([label, field, type]) => (
                      <div key={field}>
                        <label className="block text-gray-400 mb-1">{label}</label>
                        <input type={type} step={type === 'number' ? '0.01' : undefined} value={editingProduct[field]} onChange={e => setEditingProduct(prev => ({ ...prev, [field]: e.target.value }))} className={inputCls} />
                      </div>
                    ))}
                    <div className="flex items-center gap-2 mt-6">
                      <button onClick={handleSaveProduct} disabled={savingProduct} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm font-semibold uppercase tracking-wide text-xs disabled:opacity-50">
                        {savingProduct ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={() => setEditingProduct(null)} className="px-4 py-2 text-xs text-gray-400 hover:text-white">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CATALOG BULK CSV TAB */}
        {activeTab === 'catalog' && (
          <CatalogCsvTab token={token} />
        )}

        {/* VIDEOS TAB */}
        {activeTab === 'videos' && (
          <div data-testid="admin-videos">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-8">
              <h2 className="font-heading text-2xl mb-4">Add New Video</h2>
              <form onSubmit={handleCreateVideo} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-400 mb-1">Title</label>
                  <input type="text" value={newVideo.title} onChange={e => setNewVideo(p => ({...p, title: e.target.value}))} className={inputCls} required />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Category</label>
                  <select value={newVideo.category} onChange={e => setNewVideo(p => ({...p, category: e.target.value}))} className={inputCls}>
                    {['polishing101', 'beginner', 'advanced', 'tricks'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Video URL (YouTube embed)</label>
                  <input type="url" value={newVideo.video_url} onChange={e => setNewVideo(p => ({...p, video_url: e.target.value}))} className={inputCls} required />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Thumbnail URL</label>
                  <input type="url" value={newVideo.thumbnail_url} onChange={e => setNewVideo(p => ({...p, thumbnail_url: e.target.value}))} className={inputCls} />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Duration (minutes)</label>
                  <input type="number" value={newVideo.duration_minutes} onChange={e => setNewVideo(p => ({...p, duration_minutes: Number(e.target.value)}))} className={inputCls} />
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <input type="checkbox" id="vip-only" checked={newVideo.is_vip_only} onChange={e => setNewVideo(p => ({...p, is_vip_only: e.target.checked}))} className="w-4 h-4" />
                  <label htmlFor="vip-only" className="text-gray-400 text-sm">VIP Only</label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 mb-1">Related Product IDs <span className="text-gray-500 text-xs">(comma-separated, shown as upsell CTA in player)</span></label>
                  <input
                    type="text"
                    placeholder="prod_abc123, prod_def456"
                    value={(newVideo.related_product_ids || []).join(', ')}
                    onChange={e => setNewVideo(p => ({...p, related_product_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                    className={inputCls}
                    data-testid="new-video-related-products"
                  />
                </div>
                <div className="flex items-center mt-2">
                  <button type="submit" disabled={creatingVideo} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm font-semibold uppercase tracking-wide text-xs disabled:opacity-50">
                    {creatingVideo ? 'Adding...' : 'Add Video'}
                  </button>
                </div>
              </form>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">Existing Videos ({videos.length})</h2>
              {loadingVideos ? <div className="text-gray-400">Loading...</div> : (
                <div className="space-y-2">
                  {videos.map(v => (
                    <div key={v.video_id}>
                      {editingVideo?.video_id === v.video_id ? (
                        <div className="border border-[#D4AF37]/30 rounded-sm p-4 bg-[#1A1A1A] space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <label className="block text-gray-400 mb-1">Title</label>
                              <input value={editingVideo.title} onChange={e => setEditingVideo(p => ({...p, title: e.target.value}))} className={inputCls} />
                            </div>
                            <div>
                              <label className="block text-gray-400 mb-1">Category</label>
                              <select value={editingVideo.category} onChange={e => setEditingVideo(p => ({...p, category: e.target.value}))} className={inputCls}>
                                {['polishing101', 'beginner', 'advanced', 'tricks'].map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-gray-400 mb-1">Video URL</label>
                              <input value={editingVideo.video_url} onChange={e => setEditingVideo(p => ({...p, video_url: e.target.value}))} className={inputCls} />
                            </div>
                            <div>
                              <label className="block text-gray-400 mb-1">Thumbnail URL</label>
                              <input value={editingVideo.thumbnail_url || ''} onChange={e => setEditingVideo(p => ({...p, thumbnail_url: e.target.value}))} className={inputCls} />
                            </div>
                            <div>
                              <label className="block text-gray-400 mb-1">Duration (min)</label>
                              <input type="number" value={editingVideo.duration_minutes} onChange={e => setEditingVideo(p => ({...p, duration_minutes: Number(e.target.value)}))} className={inputCls} />
                            </div>
                            <div className="flex items-center gap-2 mt-4">
                              <input type="checkbox" id={`vip-edit-${v.video_id}`} checked={!!editingVideo.is_vip_only} onChange={e => setEditingVideo(p => ({...p, is_vip_only: e.target.checked}))} className="w-4 h-4" />
                              <label htmlFor={`vip-edit-${v.video_id}`} className="text-gray-400 text-sm">VIP Only</label>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-gray-400 mb-1">Related Product IDs <span className="text-gray-500 text-xs">(comma-separated)</span></label>
                              <input
                                type="text"
                                placeholder="prod_abc123, prod_def456"
                                value={(editingVideo.related_product_ids || []).join(', ')}
                                onChange={e => setEditingVideo(p => ({...p, related_product_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                                className={inputCls}
                                data-testid={`edit-video-related-products-${v.video_id}`}
                              />
                            </div>
                          </div>
                          <div className="flex gap-3 pt-2">
                            <button onClick={handleSaveVideo} disabled={savingVideo} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-1.5 rounded-sm text-xs font-bold uppercase disabled:opacity-50">{savingVideo ? 'Saving...' : 'Save Changes'}</button>
                            <button onClick={() => setEditingVideo(null)} className="text-gray-400 hover:text-white text-xs px-3 py-1.5">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between py-2.5 px-3 border border-white/5 rounded-sm hover:bg-white/5">
                          <div>
                            <p className="text-white font-semibold text-sm">{v.title}</p>
                            <p className="text-gray-500 text-xs">{v.category} · {v.duration_minutes} min{v.is_vip_only ? ' · VIP Only' : ''}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditingVideo({...v})} className="text-xs px-3 py-1 rounded-sm border border-white/20 text-gray-300 hover:border-[#D62828]" data-testid={`edit-video-${v.video_id}`}>Edit</button>
                            <button onClick={() => handleDeleteVideo(v.video_id)} className="text-xs px-3 py-1 rounded-sm border border-red-500/30 text-red-400 hover:border-red-500" data-testid={`delete-video-${v.video_id}`}>Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {videos.length === 0 && <p className="text-gray-500 text-sm py-4">No videos yet.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AFFILIATES TAB */}
        {activeTab === 'affiliates' && (
          <div data-testid="admin-affiliates">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-8">
              <h2 className="font-heading text-2xl mb-4">Add New Affiliate</h2>
              <form onSubmit={handleCreateAffiliate} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-400 mb-1">Business Name</label>
                  <input type="text" value={newAffiliate.name} onChange={e => setNewAffiliate(p => ({...p, name: e.target.value}))} className={inputCls} required />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Location</label>
                  <input type="text" value={newAffiliate.location} onChange={e => setNewAffiliate(p => ({...p, location: e.target.value}))} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 mb-1">Description</label>
                  <textarea value={newAffiliate.description} onChange={e => setNewAffiliate(p => ({...p, description: e.target.value}))} className={`${inputCls} min-h-[80px]`} />
                </div>
                {['website', 'maps', 'facebook', 'twitter', 'tiktok', 'youtube'].map(link => (
                  <div key={link}>
                    <label className="block text-gray-400 mb-1 capitalize">{link} URL</label>
                    <input type="url" value={newAffiliate.links[link]} onChange={e => setNewAffiliate(p => ({...p, links: {...p.links, [link]: e.target.value}}))} className={inputCls} placeholder={`https://...`} />
                  </div>
                ))}
                <div className="flex items-center mt-2">
                  <button type="submit" disabled={creatingAffiliate} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm font-semibold uppercase tracking-wide text-xs disabled:opacity-50">
                    {creatingAffiliate ? 'Adding...' : 'Add Affiliate'}
                  </button>
                </div>
              </form>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">Existing Affiliates ({affiliates.length})</h2>
              {loadingAffiliates ? <div className="text-gray-400">Loading...</div> : (
                <div className="space-y-2">
                  {affiliates.map(a => (
                    <div key={a.affiliate_id}>
                      {editingAffiliate?.affiliate_id === a.affiliate_id ? (
                        <div className="border border-[#D4AF37]/30 rounded-sm p-4 bg-[#1A1A1A] space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <label className="block text-gray-400 mb-1">Business Name</label>
                              <input value={editingAffiliate.name} onChange={e => setEditingAffiliate(p => ({...p, name: e.target.value}))} className={inputCls} />
                            </div>
                            <div>
                              <label className="block text-gray-400 mb-1">Location</label>
                              <input value={editingAffiliate.location || ''} onChange={e => setEditingAffiliate(p => ({...p, location: e.target.value}))} className={inputCls} />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-gray-400 mb-1">Description</label>
                              <textarea value={editingAffiliate.description || ''} onChange={e => setEditingAffiliate(p => ({...p, description: e.target.value}))} className={`${inputCls} min-h-[70px]`} />
                            </div>
                            {['website', 'maps', 'facebook', 'twitter', 'tiktok', 'youtube'].map(link => (
                              <div key={link}>
                                <label className="block text-gray-400 mb-1 capitalize">{link} URL</label>
                                <input value={editingAffiliate.links?.[link] || ''} onChange={e => setEditingAffiliate(p => ({...p, links: {...p.links, [link]: e.target.value}}))} className={inputCls} placeholder="https://..." />
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-3 pt-2">
                            <button onClick={handleSaveAffiliate} disabled={savingAffiliate} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-1.5 rounded-sm text-xs font-bold uppercase disabled:opacity-50">{savingAffiliate ? 'Saving...' : 'Save Changes'}</button>
                            <button onClick={() => setEditingAffiliate(null)} className="text-gray-400 hover:text-white text-xs px-3 py-1.5">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between py-2.5 px-3 border border-white/5 rounded-sm hover:bg-white/5">
                          <div>
                            <p className="text-white font-semibold text-sm">{a.name}</p>
                            <p className="text-gray-500 text-xs">{a.location}{a.links?.website ? ` · ${a.links.website}` : ''}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditingAffiliate({...a, links: a.links || {}})} className="text-xs px-3 py-1 rounded-sm border border-white/20 text-gray-300 hover:border-[#D62828]" data-testid={`edit-affiliate-${a.affiliate_id}`}>Edit</button>
                            <button onClick={() => handleDeleteAffiliate(a.affiliate_id)} className="text-xs px-3 py-1 rounded-sm border border-red-500/30 text-red-400 hover:border-red-500" data-testid={`delete-affiliate-${a.affiliate_id}`}>Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {affiliates.length === 0 && <p className="text-gray-500 text-sm py-4">No affiliates yet.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div data-testid="admin-customers">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl">Customers ({customers.length})</h2>
                <span className="text-xs text-gray-500 bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-1">
                  {customers.filter(c => c.is_vip).length} VIP · {customers.filter(c => !c.is_vip).length} Standard
                </span>
              </div>
              {loadingCustomers ? <div className="text-gray-400 py-4">Loading...</div> : (
                <div className="overflow-x-auto text-sm">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                        {['Name', 'Email', 'Phone', 'VIP', 'Admin', 'Joined', 'Action'].map(h => <th key={h} className="text-left py-2 pr-4">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map(c => (
                        <tr key={c.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                          <td className="py-2 pr-4 text-white">{c.name}</td>
                          <td className="py-2 pr-4 text-gray-300">{c.email}</td>
                          <td className="py-2 pr-4 text-gray-400">{c.phone || '—'}</td>
                          <td className="py-2 pr-4">
                            {c.is_vip
                              ? <span className="text-[#D4AF37] font-bold">VIP</span>
                              : <span className="text-gray-500">No</span>}
                          </td>
                          <td className="py-2 pr-4">{c.is_admin ? <span className="text-[#D62828] font-bold">Admin</span> : <span className="text-gray-500">No</span>}</td>
                          <td className="py-2 pr-4 text-gray-500 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                          <td className="py-2 pr-4">
                            {!c.is_admin && (
                              <button
                                onClick={() => handleToggleVip(c.user_id, c.is_vip, c.name || c.email)}
                                disabled={togglingVip === c.user_id}
                                data-testid={`vip-toggle-${c.user_id}`}
                                className={`text-xs px-3 py-1 rounded-sm border transition-colors disabled:opacity-40 ${c.is_vip ? 'border-red-500/40 text-red-400 hover:border-red-500 hover:bg-red-500/10' : 'border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10'}`}
                              >
                                {togglingVip === c.user_id ? '...' : c.is_vip ? 'Revoke VIP' : 'Grant VIP'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {customers.length === 0 && <p className="text-gray-500 py-4">No customers yet.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'vip' && (
          <div data-testid="admin-vip">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#121212] border border-[#D4AF37]/20 rounded-sm p-4">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Active VIP Members</p>
                <p className="font-heading text-3xl text-[#D4AF37]">{vipMembers.length}</p>
              </div>
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Expiring Soon (30d)</p>
                <p className="font-heading text-3xl text-white">
                  {vipMembers.filter(m => {
                    if (!m.vip_expires_at) return false;
                    const exp = new Date(m.vip_expires_at);
                    const diff = (exp - new Date()) / (1000 * 60 * 60 * 24);
                    return diff > 0 && diff <= 30;
                  }).length}
                </p>
              </div>
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Expired</p>
                <p className="font-heading text-3xl text-red-400">
                  {vipMembers.filter(m => m.vip_expires_at && new Date(m.vip_expires_at) < new Date()).length}
                </p>
              </div>
            </div>

            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">VIP Members</h2>
              {loadingVip ? <div className="text-gray-400 py-4">Loading...</div> : (
                <div className="overflow-x-auto text-sm">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                        {['Name', 'Email', 'VIP Expiry', 'Status', 'Joined', 'Extend / Revoke'].map(h => (
                          <th key={h} className="text-left py-2 pr-6">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {vipMembers.map(m => {
                        const expired = m.vip_expires_at && new Date(m.vip_expires_at) < new Date();
                        const expiringSoon = m.vip_expires_at && !expired && (new Date(m.vip_expires_at) - new Date()) / (1000 * 60 * 60 * 24) <= 30;
                        return (
                          <tr key={m.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/5" data-testid={`vip-member-${m.user_id}`}>
                            <td className="py-3 pr-6 text-white font-medium">{m.name || '—'}</td>
                            <td className="py-3 pr-6 text-gray-300">{m.email}</td>
                            <td className="py-3 pr-6 text-gray-400 text-xs">
                              {m.vip_expires_at ? new Date(m.vip_expires_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="py-3 pr-6">
                              {expired
                                ? <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-sm font-bold">Expired</span>
                                : expiringSoon
                                  ? <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-sm font-bold">Expiring Soon</span>
                                  : <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-sm font-bold">Active</span>
                              }
                            </td>
                            <td className="py-3 pr-6 text-gray-500 text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</td>
                            <td className="py-3 pr-6">
                              <div className="flex items-center gap-2">
                                <select
                                  value={extendMonths[m.user_id] || 1}
                                  onChange={e => setExtendMonths(prev => ({ ...prev, [m.user_id]: e.target.value }))}
                                  className="bg-[#1A1A1A] border border-white/10 text-gray-300 rounded-sm px-2 py-1 text-xs"
                                  data-testid={`extend-months-${m.user_id}`}
                                >
                                  {[1, 3, 6, 12].map(n => <option key={n} value={n}>{n}mo</option>)}
                                </select>
                                <button
                                  onClick={() => handleExtendVip(m.user_id, m.name || m.email)}
                                  disabled={extendingVip === m.user_id}
                                  data-testid={`extend-vip-${m.user_id}`}
                                  className="text-xs px-3 py-1 rounded-sm border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40"
                                >
                                  {extendingVip === m.user_id ? '...' : 'Extend'}
                                </button>
                                <button
                                  onClick={() => handleToggleVip(m.user_id, true, m.name || m.email)}
                                  disabled={togglingVip === m.user_id}
                                  data-testid={`revoke-vip-${m.user_id}`}
                                  className="text-xs px-3 py-1 rounded-sm border border-red-500/40 text-red-400 hover:border-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                                >
                                  {togglingVip === m.user_id ? '...' : 'Revoke'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {vipMembers.length === 0 && (
                    <div className="text-center py-10">
                      <p className="text-gray-500">No VIP members yet.</p>
                      <p className="text-gray-600 text-xs mt-1">Go to the Customers tab to manually grant VIP access.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div data-testid="admin-orders">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">Orders ({orders.length})</h2>
              {loadingOrders ? <div className="text-gray-400 py-4">Loading...</div> : (
                <div className="space-y-3">
                  {orders.map(order => (
                    <div key={order.order_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
                        <div>
                          <p className="text-white font-semibold text-sm">Order #{order.order_id}</p>
                          <p className="text-gray-500 text-xs">{order.user_email || order.user_id} · {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</p>
                          <p className="text-[#D62828] font-bold">${order.total?.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${order.order_status === 'confirmed' ? 'bg-green-500/20 text-green-400' : order.order_status === 'shipped' ? 'bg-blue-500/20 text-blue-400' : order.order_status === 'delivered' ? 'bg-purple-500/20 text-purple-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {order.order_status}
                          </span>
                          <select
                            value={order.order_status}
                            onChange={e => handleUpdateOrderStatus(order.order_id, e.target.value)}
                            disabled={updatingOrder === order.order_id}
                            className="bg-[#1A1A1A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs"
                          >
                            {['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                        {order.items?.map((item, i) => <span key={i}>{item.name} ×{item.quantity}</span>)}
                      </div>
                    </div>
                  ))}
                  {orders.length === 0 && <p className="text-gray-500">No orders yet.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'testimonials' && (
          <div data-testid="admin-testimonials">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl">Add Testimonial</h2>
                <button onClick={handleSeedTestimonials} className="text-xs px-3 py-1.5 border border-white/20 text-gray-300 rounded-sm hover:border-white">Seed Sample Data</button>
              </div>
              <form onSubmit={handleCreateTestimonial} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-400 mb-1">Author Name</label>
                  <input type="text" value={newTestimonial.author} onChange={e => setNewTestimonial(p => ({...p, author: e.target.value}))} className={inputCls} required />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Location (optional)</label>
                  <input type="text" value={newTestimonial.location} onChange={e => setNewTestimonial(p => ({...p, location: e.target.value}))} className={inputCls} placeholder="e.g. Texas" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 mb-1">Testimonial Content</label>
                  <textarea value={newTestimonial.content} onChange={e => setNewTestimonial(p => ({...p, content: e.target.value}))} className={`${inputCls} min-h-[100px]`} required />
                </div>
                <div>
                  <label className="block text-gray-400 mb-1">Rating (1-5)</label>
                  <select value={newTestimonial.rating} onChange={e => setNewTestimonial(p => ({...p, rating: Number(e.target.value)}))} className={inputCls}>
                    {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <button type="submit" disabled={creatingTestimonial} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-2 rounded-sm font-semibold uppercase tracking-wide text-xs disabled:opacity-50">
                    {creatingTestimonial ? 'Adding...' : 'Add Testimonial'}
                  </button>
                </div>
              </form>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-4">Existing Testimonials ({testimonials.length})</h2>
              {loadingTestimonials ? <div className="text-gray-400">Loading...</div> : (
                <div className="space-y-3">
                  {testimonials.map(t => (
                    <div key={t.testimonial_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">{t.author} {t.location && <span className="text-gray-500 font-normal">— {t.location}</span>}</p>
                        <p className="text-gray-400 text-sm mt-1">"{t.content}"</p>
                        <p className="text-[#D4AF37] text-xs mt-1">{'★'.repeat(t.rating)}</p>
                      </div>
                      <button onClick={() => handleDeleteTestimonial(t.testimonial_id)} className="text-xs px-3 py-1 rounded-sm border border-red-500/30 text-red-400 hover:border-red-500 flex-shrink-0">Delete</button>
                    </div>
                  ))}
                  {testimonials.length === 0 && <p className="text-gray-500 text-sm">No testimonials yet. Click "Seed Sample Data" to add some.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-6" data-testid="admin-reviews-content">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-1">Customer Reviews ({reviews.length})</h2>
              <p className="text-gray-500 text-sm mb-6">Delete reviews that violate guidelines. All reviews auto-publish on submission.</p>
              {loadingReviews ? <div className="text-gray-400">Loading...</div> : reviews.length === 0 ? (
                <p className="text-gray-500 text-sm">No customer reviews yet.</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map(r => (
                    <div key={r.review_id} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4" data-testid={`admin-review-${r.review_id}`}>
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-[#D4AF37]">{'★'.repeat(r.rating)}<span className="text-gray-700">{'★'.repeat(5 - r.rating)}</span></span>
                            {r.title && <span className="text-white font-semibold text-sm">{r.title}</span>}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-2 mb-2">
                            <span>{r.user_name}</span>
                            <span>·</span>
                            <span>Product: <span className="text-gray-300 font-mono">{r.product_id}</span></span>
                            {r.verified_purchase && (
                              <span className="text-green-400 ml-1">✓ Verified Purchase</span>
                            )}
                            <span>·</span>
                            <span>{new Date(r.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-gray-300 text-sm whitespace-pre-line">{r.body}</p>
                        </div>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete this review permanently?')) return;
                            try {
                              await axios.delete(`${API}/admin/reviews/${r.review_id}`, { headers: { Authorization: `Bearer ${token}` } });
                              setReviews(prev => prev.filter(x => x.review_id !== r.review_id));
                              toast.success('Review deleted');
                            } catch {
                              toast.error('Failed to delete review');
                            }
                          }}
                          className="text-xs px-3 py-1 rounded-sm border border-red-500/30 text-red-400 hover:border-red-500 hover:bg-red-500/10 flex-shrink-0"
                          data-testid={`admin-review-delete-${r.review_id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'promo' && (
          <div className="space-y-6" data-testid="promo-admin-panel">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-2">Grand Opening Promotion</h2>
              <p className="text-gray-400 text-sm mb-6">Controls the site-wide launch promotion. When active, new signups receive free VIP for the configured number of months and the discount is automatically applied to every cart and checkout.</p>
              {loadingPromo || !promo ? (
                <p className="text-gray-400">Loading...</p>
              ) : (
                <div className="space-y-4 max-w-xl">
                  <label className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={!!promo.grand_opening_active}
                      onChange={(e) => setPromo({ ...promo, grand_opening_active: e.target.checked })}
                      className="w-5 h-5 accent-[#D62828]"
                      data-testid="promo-active-toggle"
                    />
                    <span className="text-white">Promotion is active</span>
                  </label>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Discount Percent</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={promo.discount_percent ?? 0}
                      onChange={(e) => setPromo({ ...promo, discount_percent: Number(e.target.value) })}
                      className="w-full bg-[#0F0F0F] border border-white/10 text-white rounded-sm h-11 px-3"
                      data-testid="promo-discount-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Free VIP Months on Signup</label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      step="1"
                      value={promo.free_vip_months ?? 0}
                      onChange={(e) => setPromo({ ...promo, free_vip_months: Number(e.target.value) })}
                      className="w-full bg-[#0F0F0F] border border-white/10 text-white rounded-sm h-11 px-3"
                      data-testid="promo-vip-months-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">End Date (optional, ISO format e.g. 2026-12-31)</label>
                    <input
                      type="date"
                      value={promo.end_date ? promo.end_date.slice(0, 10) : ''}
                      onChange={(e) => setPromo({ ...promo, end_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      className="w-full bg-[#0F0F0F] border border-white/10 text-white rounded-sm h-11 px-3"
                      data-testid="promo-end-date-input"
                    />
                    {promo.end_date && (
                      <button
                        type="button"
                        onClick={() => setPromo({ ...promo, end_date: null })}
                        className="mt-1 text-xs text-gray-400 hover:text-white underline"
                      >Clear end date</button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Banner Text</label>
                    <input
                      type="text"
                      value={promo.banner_text || ''}
                      onChange={(e) => setPromo({ ...promo, banner_text: e.target.value })}
                      className="w-full bg-[#0F0F0F] border border-white/10 text-white rounded-sm h-11 px-3"
                      data-testid="promo-banner-input"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      setSavingPromo(true);
                      try {
                        const payload = {
                          grand_opening_active: !!promo.grand_opening_active,
                          discount_percent: Number(promo.discount_percent) || 0,
                          free_vip_months: Number(promo.free_vip_months) || 0,
                          end_date: promo.end_date || null,
                          banner_text: promo.banner_text || '',
                        };
                        const r = await axios.put(`${API}/admin/promo`, payload, { headers: { Authorization: `Bearer ${token}` } });
                        setPromo(r.data);
                        toast.success('Promotion saved');
                      } catch (err) {
                        toast.error('Failed to save promotion');
                      }
                      setSavingPromo(false);
                    }}
                    disabled={savingPromo}
                    className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                    data-testid="promo-save-btn"
                  >
                    {savingPromo ? 'Saving...' : 'Save Promotion'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'wholesale' && (
          <div className="space-y-6" data-testid="wholesale-admin-panel">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-2">Wholesale / B2B</h2>
              <p className="text-gray-400 text-sm mb-6">Review business applications, approve/reject, and assign discount tiers. Approved accounts see wholesale pricing site-wide and can choose pay-now or NET-30 invoice at checkout.</p>
              {loadingWholesale ? (
                <p className="text-gray-400">Loading...</p>
              ) : (
                <>
                  <div className="mb-6">
                    <h3 className="font-heading text-lg mb-3">Tiers</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {wholesaleTiers.map(t => (
                        <div key={t.tier} className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4" data-testid={`admin-wholesale-tier-${t.tier}`}>
                          <p className="text-xs uppercase text-gray-400">Tier {t.tier} — {t.name}</p>
                          <p className="text-2xl font-heading text-green-400">{t.percent_off}% OFF</p>
                          <p className="text-xs text-gray-500">on orders ${t.min_subtotal.toFixed(2)}+</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <h3 className="font-heading text-lg mb-3">Applications ({wholesaleApps.length})</h3>
                  {wholesaleApps.length === 0 ? (
                    <p className="text-gray-500 text-sm">No applications yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {wholesaleApps.map(a => (
                        <div key={a.application_id} className="bg-[#0F0F0F] border border-white/10 rounded-sm p-4" data-testid={`admin-wholesale-app-${a.application_id}`}>
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-[260px]">
                              <p className="text-white font-semibold">{a.business_name} <span className={`text-xs ml-2 px-2 py-0.5 rounded-sm uppercase ${a.status === 'approved' ? 'bg-green-500/20 text-green-400' : a.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{a.status}{a.tier ? ` · Tier ${a.tier}` : ''}</span></p>
                              <p className="text-gray-400 text-xs mt-1">{a.user_name} • {a.user_email}</p>
                              <p className="text-gray-400 text-xs">EIN: {a.ein} • {a.contact_phone}</p>
                              <p className="text-gray-400 text-xs">{a.business_address}</p>
                              {a.resale_cert_note && <p className="text-gray-500 text-xs mt-1 italic">Cert: {a.resale_cert_note}</p>}
                              {a.estimated_monthly_volume && <p className="text-gray-500 text-xs">Volume est: {a.estimated_monthly_volume}</p>}
                              {a.notes && <p className="text-gray-500 text-xs italic">"{a.notes}"</p>}
                              <p className="text-gray-600 text-xs mt-1">Submitted {new Date(a.applied_at).toLocaleDateString()}</p>
                            </div>
                            {a.status === 'pending' && (
                              <button
                                onClick={() => { setReviewingApp(a); setReviewTier(1); setReviewNotes(''); }}
                                className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-4 py-2 rounded-sm text-xs font-semibold uppercase tracking-wide"
                                data-testid={`admin-review-${a.application_id}`}
                              >
                                Review
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {reviewingApp && (
              <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setReviewingApp(null)}>
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-review-modal">
                  <h3 className="font-heading text-2xl mb-2">Review: {reviewingApp.business_name}</h3>
                  <div className="mb-4 space-y-2">
                    <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Assign Tier (for approval)</label>
                    <select value={reviewTier} onChange={(e) => setReviewTier(Number(e.target.value))} className="w-full bg-[#0F0F0F] border border-white/10 text-white rounded-sm h-11 px-3" data-testid="admin-review-tier">
                      {wholesaleTiers.map(t => (
                        <option key={t.tier} value={t.tier}>Tier {t.tier} — {t.name} ({t.percent_off}% off, ${t.min_subtotal}+)</option>
                      ))}
                    </select>
                    <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1 mt-3">Notes (optional)</label>
                    <textarea rows={3} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 text-white rounded-sm p-3" placeholder="Reason for rejection or note for customer" data-testid="admin-review-notes" />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/admin/wholesale/applications/${reviewingApp.application_id}`, { status: 'approved', tier: reviewTier, review_notes: reviewNotes || null }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Application approved');
                          setReviewingApp(null);
                          const r = await axios.get(`${API}/admin/wholesale/applications`, { headers: { Authorization: `Bearer ${token}` } });
                          setWholesaleApps(r.data.applications || []);
                        } catch (e) {
                          toast.error('Approval failed');
                        }
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-sm font-semibold uppercase text-xs tracking-wide"
                      data-testid="admin-review-approve"
                    >
                      Approve
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/admin/wholesale/applications/${reviewingApp.application_id}`, { status: 'rejected', review_notes: reviewNotes || null }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Application rejected');
                          setReviewingApp(null);
                          const r = await axios.get(`${API}/admin/wholesale/applications`, { headers: { Authorization: `Bearer ${token}` } });
                          setWholesaleApps(r.data.applications || []);
                        } catch (e) {
                          toast.error('Rejection failed');
                        }
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-sm font-semibold uppercase text-xs tracking-wide"
                      data-testid="admin-review-reject"
                    >
                      Reject
                    </button>
                    <button onClick={() => setReviewingApp(null)} className="px-4 border border-white/20 text-gray-300 rounded-sm text-xs uppercase tracking-wide">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'referrals' && (
          <div className="space-y-6" data-testid="referrals-admin-panel">
            <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
              <h2 className="font-heading text-2xl mb-2">Referral Program</h2>
              <p className="text-gray-400 text-sm mb-6">$10 store credit is auto-issued to a referrer when their referee completes their first paid order. Credits are then auto-applied at the referrer's checkout if they opt in.</p>
              {loadingReferrals || !referralData ? (
                <p className="text-gray-400">Loading...</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4">
                      <p className="text-xs uppercase text-gray-400 mb-1">Total Credits Issued</p>
                      <p className="text-2xl font-heading text-green-400" data-testid="admin-referrals-total">${referralData.total_issued.toFixed(2)}</p>
                    </div>
                    <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4">
                      <p className="text-xs uppercase text-gray-400 mb-1">Successful Referrals</p>
                      <p className="text-2xl font-heading text-white" data-testid="admin-referrals-count">{referralData.count}</p>
                    </div>
                  </div>
                  {referralData.credits.length === 0 ? (
                    <p className="text-gray-500 text-sm">No referral credits issued yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="text-xs text-gray-400 uppercase border-b border-white/10">
                          <tr>
                            <th className="py-2 pr-4">Referrer</th>
                            <th className="py-2 pr-4">Referee</th>
                            <th className="py-2 pr-4">Order</th>
                            <th className="py-2 pr-4">Amount</th>
                            <th className="py-2 pr-4">Issued</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referralData.credits.map(c => (
                            <tr key={c.credit_id} className="border-b border-white/5" data-testid={`admin-referral-${c.credit_id}`}>
                              <td className="py-2 pr-4 text-white">{c.referrer_name}<br /><span className="text-gray-500 text-xs">{c.referrer_email}</span></td>
                              <td className="py-2 pr-4 text-white">{c.referee_name}<br /><span className="text-gray-500 text-xs">{c.referee_email}</span></td>
                              <td className="py-2 pr-4 text-gray-400 text-xs font-mono">{c.trigger_order_id}</td>
                              <td className="py-2 pr-4 text-green-400 font-semibold">${c.amount.toFixed(2)}</td>
                              <td className="py-2 pr-4 text-gray-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
